// The lesson coach: what step you're on, what to do, a checklist. It sits in
// the Playground's side panel for the whole lesson. See Design > Console,
// "Lessons".

import type { Lesson } from "../../domain/lesson.ts";
import { revert, skipLesson } from "../actions.ts";
import { href, navigate } from "../router.ts";
import { Button, Coach as KitCoach } from "../../../../ui/src/index.ts";

const Illustration = ({ kind, step }: { kind: Lesson["illustration"]; step: number }) => {
  const sky = "var(--wh-primary)";
  const tint = "var(--wh-sky-300)";
  const ok = "var(--wh-accent)";
  switch (kind) {
    case "bid":
      return <svg viewBox="0 0 340 52" role="img" aria-label="A bid bar next to an ask bar"><rect x="24" y="14" width={step >= 2 ? 150 : 90} height="9" rx="4" fill={tint} /><rect x="24" y="32" width="150" height="9" rx="4" fill={sky} /><line x1="174" y1="8" x2="174" y2="46" stroke="var(--wh-foreground)" stroke-width="2" /></svg>;
    case "missing":
      return <svg viewBox="0 0 340 52" role="img" aria-label="A question with a blank to fill in"><rect x="24" y="16" width="88" height="20" rx="5" fill={tint} stroke={sky} stroke-width="2" /><rect x="124" y="16" width="88" height="20" rx="5" fill={step >= 2 ? ok : "none"} stroke={ok} stroke-width="2" stroke-dasharray={step >= 2 ? undefined : "5 4"} /><rect x="224" y="16" width="88" height="20" rx="5" fill={tint} stroke={sky} stroke-width="2" /></svg>;
    case "scope":
      return <svg viewBox="0 0 340 52" role="img" aria-label="A question outside a hub's circle, then inside it"><circle cx="110" cy="26" r="20" fill={tint} stroke={sky} stroke-width="2" /><circle cx={step >= 2 ? 110 : 250} cy="26" r="6" fill={ok} /></svg>;
    case "refund":
      return <svg viewBox="0 0 340 52" role="img" aria-label="An arrow returning credits"><path d="M120 18a14 14 0 1 1-4 18" fill="none" stroke={sky} stroke-width="2" /><path d="M112 12v10h10" fill="none" stroke={sky} stroke-width="2" /><circle cx="170" cy="26" r="7" fill={step >= 2 ? ok : "none"} stroke={ok} stroke-width="2" /></svg>;
    case "answer":
      return <svg viewBox="0 0 340 52" role="img" aria-label="Three steps">{[0, 1, 2].map((i) => <circle key={i} cx={60 + i * 110} cy="26" r="11" fill={i < step ? ok : "var(--wh-card)"} stroke={i < step ? ok : sky} stroke-width="2" />)}<line x1="71" y1="26" x2="159" y2="26" stroke={tint} stroke-width="2" /><line x1="181" y1="26" x2="269" y2="26" stroke={tint} stroke-width="2" /></svg>;
    default:
      return kind satisfies never;
  }
};

type Props = {
  lesson: Lesson;
  step: number;
  done: boolean;
  mismatch: boolean;
  coach: string;
  items: { label: string; done: boolean }[];
};

// Lesson eyebrows arrive in capitals (domain/examples.ts); the kit sets labels in sentence case.
const sentence = (text: string) => text.charAt(0) + text.slice(1).toLowerCase();

/** Leaves the lesson: the request and any response stay, outside the lesson. */
export function exitLesson() {
  skipLesson();
  navigate("playground");
}

export function Coach({ lesson, step, done, mismatch, coach, items }: Props) {
  return (
    <KitCoach class="cs-coach" label="Lesson coach" eyebrow={sentence(lesson.eyebrow)} title={lesson.title}
      art={<Illustration kind={lesson.illustration} step={step} />} steps={items}
      actions={<>
        {mismatch && <Button size="sm" onClick={revert}>Restart lesson</Button>}
        {done && (lesson.next
          ? <Button size="sm" variant="solid" href={href(`playground/${lesson.next}`)}>Next lesson</Button>
          : <Button size="sm" variant="solid" onClick={exitLesson}>Finish</Button>)}
      </>}>
      <p>{coach}</p>
    </KitCoach>
  );
}
